/* eslint-disable no-console */
const JSLogger = require("js-logger");
const Sentry = require("@sentry/browser");
const { SENTRY_IGNORE_ERRORS } = require("../../web/config/sentry.js");
const {
  buildErrorReport,
  createErrorCaptureGuard,
  getOriginalError,
  isErrorIgnored,
} = require("./loggerErrorHelper");
const {
  getEnvironmentFromRequestorClientId,
  getSentryEnvironmentFromHostname,
} = require("../config/utils");

// eslint-disable-next-line react-hooks/rules-of-hooks
JSLogger.useDefaults();
const shouldCaptureError = createErrorCaptureGuard();

const debug = (...logs) => {
  JSLogger.debug(logs.join());
};

const log = (...logs) => {
  JSLogger.debug(logs.join());
};

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

const isInfoOptions = (value) =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  (hasOwn(value, "passToSentry") ||
    hasOwn(value, "tag") ||
    hasOwn(value, "extra") ||
    hasOwn(value, "level") ||
    hasOwn(value, "title"));

const info = (...logs) => {
  let infoOptions = null;
  let logEntries = logs;

  if (logs.length > 0 && isInfoOptions(logs[logs.length - 1])) {
    infoOptions = logs[logs.length - 1];
    logEntries = logs.slice(0, -1);
  }

  const message = logEntries.join();

  if (typeof window === "undefined") {
    const logEntry = {
      level: "info",
      timestamp: new Date().toISOString(),
      message,
    };

    const output = JSON.stringify(logEntry);

    // @ts-ignore
    process.stdout.write(`${output}\n`);
  } else {
    JSLogger.info(message);
  }

  if (!infoOptions?.passToSentry) {
    return;
  }

  const domain =
    typeof window !== "undefined" && window.location
      ? window.location.hostname
      : undefined;
  const env = domain
    ? { label: getSentryEnvironmentFromHostname(domain) }
    : getEnvironmentFromRequestorClientId();
  const defaultExtra = {
    url:
      typeof window !== "undefined" && window.location
        ? window.location.href
        : undefined,
    onServer: typeof window === "undefined",
  };
  const extra = { ...defaultExtra, ...(infoOptions?.extra || {}) };
  const tags = infoOptions?.tag ? { [infoOptions.tag]: "1" } : {};

  Sentry.withScope((scope) => {
    scope.setTag("environment", env.label);
    if (domain) {
      scope.setTag("domain", domain);
    }
    scope.setExtra("details", extra);
    Object.entries(tags).forEach(([key, value]) => {
      if (value != null) {
        scope.setTag(key, String(value));
      }
    });
    scope.setLevel(infoOptions?.level || "info");
    Sentry.captureMessage(infoOptions?.title || message);
  });
};

const warn = (...logs) => {
  JSLogger.warn(logs.join());
};

/**
 * Log error to Sentry and console.
 * Supports old signature: Logger.error(...logs) - all args are sent to Sentry.
 * Supports new signature:
 * Logger.error(message, { tags, extra, title, cause, requestEndpoint, reportedTransportError }).
 *
 * @param {string} message - Primary error message
 * @param {Object|*} [optionsOrRest] - Sentry options OR additional data (legacy)
 * @param {...*} [rest] - Additional args (legacy, all included in Sentry extra)
 *
 * Options object (when has tags/extra/title/cause/requestEndpoint/reportedTransportError):
 * @param {string} [options.title] - Clear, human-readable title for Sentry (default: message)
 * @param {Object} [options.tags] - Tags for Sentry (e.g. { area: "setup", environment: "PROD" })
 * @param {Object} [options.extra] - Custom structured data for Sentry (plain objects, not stringified)
 * @param {Error} [options.cause] - Original request error used for grouping and duplicate suppression
 * @param {string} [options.requestEndpoint] - Stable endpoint name for requests with dynamic URLs
 * @param {boolean} [options.reportedTransportError] - Mark this transport error as already reported
 */
const error = (message, optionsOrRest, ...rest) => {
  const originalError = getOriginalError(message, optionsOrRest, ...rest);
  const errorMessage =
    typeof message === "string" ? message : message?.message ?? String(message);
  const ignoredErrorMessage = originalError?.message || errorMessage;

  if (
    typeof ignoredErrorMessage === "string" &&
    isErrorIgnored(ignoredErrorMessage, SENTRY_IGNORE_ERRORS)
  ) {
    console.error(ignoredErrorMessage);
    return;
  }

  const { title, tags, customExtra, fingerprint } = buildErrorReport(
    message,
    optionsOrRest,
    ...rest
  );
  const shouldCapture = shouldCaptureError(originalError, {
    reportedTransportError: optionsOrRest?.reportedTransportError === true,
    isCause: optionsOrRest?.cause === originalError,
  });

  const defaultExtra = {
    url:
      typeof window !== "undefined" && window.location
        ? window.location.href
        : undefined,
    onServer: typeof window === "undefined",
  };

  const domain =
    typeof window !== "undefined" && window.location
      ? window.location.hostname
      : undefined;

  const env = domain
    ? { label: getSentryEnvironmentFromHostname(domain) }
    : getEnvironmentFromRequestorClientId();

  if (shouldCapture) {
    Sentry.withScope((scope) => {
      scope.setTag("environment", env.label);
      if (domain) {
        scope.setTag("domain", domain);
      }
      scope.setExtra("details", { ...defaultExtra, ...customExtra });
      Object.entries(tags).forEach(([key, value]) => {
        if (value != null) scope.setTag(key, String(value));
      });
      if (fingerprint) {
        scope.setFingerprint(fingerprint);
      }
      Sentry.captureException(new Error(String(title)));
    });
  }

  const details = { ...defaultExtra, ...customExtra };
  JSLogger.error(title, JSON.stringify(details));
};

module.exports = {
  debug,
  log,
  info,
  warn,
  error,
};
